import { SHEEP_TILE_TYPES } from '@/data/levels';
import { resolvePatternIndex } from '@/data/boardPatterns';
import { GameSettings, PropKind } from '@/data/settings';
import { BoardMode } from '@/data/board';
import {
  GameState, applyMove, requestProp, pauseRun, resumeRun, tickTime,
  endRun, restartRun, cancelQuiz, changeQuestion, answerQuiz, claimQuiz, startModeRun
} from '@/utils/gameEngine';

export const MAX_LEVEL = 4;
export type GameAction =
  | { type: 'START'; level: number; now: number; settings?: GameSettings; seed?: number }
  | { type: 'START_MODE'; mode: BoardMode; now: number; settings: GameSettings; stage?: number }
  | { type: 'CLICK'; cardId: string; now: number; runId?: string }
  | { type: 'USE_PROP'; kind: PropKind; now: number }
  | { type: 'UNDO' } | { type: 'SHUFFLE' } | { type: 'REVIVE'; now: number }
  | { type: 'PAUSE' | 'RESUME' | 'TICK' | 'RESTART' | 'EXIT_HOME' | 'NEXT_LEVEL'; now: number; runId?: string; settings?: GameSettings; changeLayout?: boolean }
  | { type: 'QUIZ_CANCEL'; now: number }
  | { type: 'QUIZ_CHANGE'; requestId: string }
  | { type: 'QUIZ_CLAIM'; requestId: string; now: number }
  | { type: 'QUIZ_ANSWER'; requestId: string; questionId: string; selected: number; now: number };

export const gameReducer = (s: GameState, a: GameAction): GameState => {
  if ('runId' in a && a.runId && a.runId !== s.runId) return s;
  switch (a.type) {
    case 'START_MODE':
      return s.phase === 'ready' ? startModeRun(a.mode, SHEEP_TILE_TYPES, a.now, a.settings, a.stage) : s;
    case 'START':
      return s.phase === 'ready' ? startModeRun('daily', SHEEP_TILE_TYPES, a.now, a.settings) : s;
    case 'NEXT_LEVEL':
      if (s.phase === 'won' && s.boardMode) return startModeRun(s.boardMode, SHEEP_TILE_TYPES, a.now, a.settings || s.settings,
        s.boardMode === 'world' ? (s.worldStage || 1) + 1 : 1,
        s.boardMode === 'world' ? (s.layoutId ? resolvePatternIndex(s.layoutIndex ?? 0, s.layoutId) + 1 : 0) : resolvePatternIndex(s.layoutIndex ?? 0, s.layoutId));
      return s.phase === 'won' ? startModeRun('daily', SHEEP_TILE_TYPES, a.now, a.settings || s.settings) : s;
    case 'RESTART':
      if (!['playing', 'paused', 'failed'].includes(s.phase)) return s;
      return s.boardMode ? restartRun(s, SHEEP_TILE_TYPES, a.now, s.level, a.settings?.props || s.initialProps, a.settings || s.settings, a.changeLayout)
        : { ...startModeRun('daily', SHEEP_TILE_TYPES, a.now, a.settings || s.settings), lastSettledRun: endRun(s, a.now).lastSettledRun };
    case 'CLICK': return applyMove(s, a.cardId, a.now);
    case 'USE_PROP': return requestProp(s, a.kind, a.now);
    case 'UNDO': return requestProp(s, 'undo', s.lastResumeAt ?? 0);
    case 'SHUFFLE': return requestProp(s, 'shuffle', s.lastResumeAt ?? 0);
    case 'REVIVE': return requestProp(s, 'revive', a.now);
    case 'PAUSE': return pauseRun(s, a.now);
    case 'RESUME': return resumeRun(s, a.now);
    case 'TICK': return tickTime(s, a.now);
    case 'EXIT_HOME': return endRun(s, a.now);
    case 'QUIZ_CANCEL': return cancelQuiz(s, a.now);
    case 'QUIZ_CHANGE': return changeQuestion(s, a.requestId);
    case 'QUIZ_CLAIM': return claimQuiz(s, a.requestId, a.now);
    case 'QUIZ_ANSWER': return answerQuiz(s, a.requestId, a.selected, a.now, a.questionId);
    default: return s;
  }
};
